import React, { useMemo, useState } from 'react';
import {
  Bot,
  Lightbulb,
  FileText,
  Search,
  Copy,
  Trash2,
  Loader2,
} from 'lucide-react';
import { api } from '../services/api';
import {
  COLD_START_MESSAGE,
  requestWithColdStartRetry,
} from '../services/coldStartRetry';
import './AIInterface.css';

const INLINE_MD_RE =
  /(`[^`]+`)|(\*\*[^*]+?\*\*)|(__[^_]+?__)|(\*[^*\n]+?\*)|(_[^_\n]+?_)/;

function renderInlineMarkdown(text) {
  if (!text) return null;

  const nodes = [];
  let rest = String(text);
  let key = 0;

  while (rest.length > 0) {
    const match = rest.match(INLINE_MD_RE);
    if (!match) {
      nodes.push(rest);
      break;
    }

    const idx = match.index || 0;
    if (idx > 0) nodes.push(rest.slice(0, idx));

    const token = match[0];
    if (token.startsWith('`')) {
      nodes.push(
        <code key={`c-${key++}`} className="md-inline-code">
          {token.slice(1, -1)}
        </code>
      );
    } else if (token.startsWith('**') || token.startsWith('__')) {
      nodes.push(<strong key={`b-${key++}`}>{token.slice(2, -2)}</strong>);
    } else if (token.startsWith('*') || token.startsWith('_')) {
      nodes.push(<em key={`i-${key++}`}>{token.slice(1, -1)}</em>);
    } else {
      nodes.push(token);
    }

    rest = rest.slice(idx + token.length);
  }

  return nodes;
}

function renderMarkdown(markdown) {
  const text = String(markdown || '').replace(/\r\n/g, '\n');
  if (!text.trim()) return null;

  const lines = text.split('\n');
  const blocks = [];
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (!line || !line.trim()) {
      i += 1;
      continue;
    }

    const trimmed = line.trim();

    if (trimmed.startsWith('```')) {
      const codeLines = [];
      i += 1;
      while (i < lines.length && !lines[i].trim().startsWith('```')) {
        codeLines.push(lines[i]);
        i += 1;
      }
      if (i < lines.length) i += 1;

      blocks.push(
        <pre key={`pre-${key++}`} className="md-code">
          <code>{codeLines.join('\n')}</code>
        </pre>
      );
      continue;
    }

    const hMatch = line.match(/^(#{1,6})\s+(.*)$/);
    if (hMatch) {
      const level = Math.min(hMatch[1].length, 6);
      const content = (hMatch[2] || '').trim();
      const Tag = `h${level}`;

      blocks.push(
        React.createElement(
          Tag,
          { key: `h-${key++}`, className: `md-h md-h${level}` },
          renderInlineMarkdown(content)
        )
      );
      i += 1;
      continue;
    }

    if (line.match(/^\s*>\s+/)) {
      const quoteLines = [];
      while (i < lines.length && lines[i].match(/^\s*>\s+/)) {
        quoteLines.push(lines[i].replace(/^\s*>\s+/, ''));
        i += 1;
      }

      blocks.push(
        <blockquote key={`bq-${key++}`} className="md-blockquote">
          {renderMarkdown(quoteLines.join('\n'))}
        </blockquote>
      );
      continue;
    }

    if (line.match(/^\s*[-*+]\s+/)) {
      const items = [];
      while (i < lines.length && lines[i].match(/^\s*[-*+]\s+/)) {
        const itemText = lines[i].replace(/^\s*[-*+]\s+/, '').trim();
        items.push(
          <li key={`li-${key++}`} className="md-li">
            {renderInlineMarkdown(itemText)}
          </li>
        );
        i += 1;
      }

      blocks.push(
        <ul key={`ul-${key++}`} className="md-ul">
          {items}
        </ul>
      );
      continue;
    }

    if (line.match(/^\s*\d+\.\s+/)) {
      const items = [];
      while (i < lines.length && lines[i].match(/^\s*\d+\.\s+/)) {
        const itemText = lines[i].replace(/^\s*\d+\.\s+/, '').trim();
        items.push(
          <li key={`oli-${key++}`} className="md-li">
            {renderInlineMarkdown(itemText)}
          </li>
        );
        i += 1;
      }

      blocks.push(
        <ol key={`ol-${key++}`} className="md-ol">
          {items}
        </ol>
      );
      continue;
    }

    const paraLines = [line];
    i += 1;
    while (
      i < lines.length &&
      lines[i].trim() &&
      !lines[i].trim().startsWith('```') &&
      !lines[i].match(/^(#{1,6})\s+/) &&
      !lines[i].match(/^\s*>\s+/) &&
      !lines[i].match(/^\s*[-*+]\s+/) &&
      !lines[i].match(/^\s*\d+\.\s+/)
    ) {
      paraLines.push(lines[i]);
      i += 1;
    }

    const paraText = paraLines.join(' ').trim();
    if (paraText) {
      blocks.push(
        <p key={`p-${key++}`} className="md-p">
          {renderInlineMarkdown(paraText)}
        </p>
      );
    }
  }

  return blocks;
}

const SUGGESTED_QUESTIONS = [
  'Quais são os 3 pontos mais importantes deste conteúdo?',
  'Explique este tema como se eu estivesse estudando para prova.',
  'Crie um resumo em tópicos curtos e objetivos.',
];

const ACTION_TITLES = {
  explain: 'Explicação da Página',
  summarize: 'Resumo do Documento',
  search: 'Busca no PDF',
  ask: 'Resposta IA',
};

const AIInterface = ({ pdfData, currentPage, setIsLoadingAnswer, setAnswerText }) => {
  const [question, setQuestion] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [warmingUp, setWarmingUp] = useState(false);
  const [response, setResponse] = useState(null);
  const [activeAction, setActiveAction] = useState(null);
  const [copied, setCopied] = useState(false);
  const [chatHistory, setChatHistory] = useState([]);

  const currentPageData = pdfData?.pages?.find(
    (page) => page.pageNumber === currentPage
  );

  const renderedResponse = useMemo(() => {
    return renderMarkdown(response?.content || '');
  }, [response?.content]);

  const askPlaceholder = useMemo(() => {
    if (currentPageData?.text && String(currentPageData.text).trim()) {
      return `Ex.: "Quais conceitos-chave aparecem na página ${currentPage}?"`;
    }
    return 'Ex.: "Resuma este PDF em 5 pontos práticos."';
  }, [currentPage, currentPageData?.text]);

  const clearAnswer = () => {
    setResponse(null);
    setCopied(false);
    setChatHistory([]);
    setAnswerText?.('');
  };

  const copyAnswer = async () => {
    const text = String(response?.content || '').trim();
    if (!text) return;

    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch (err) {
      try {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';
        textarea.style.top = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        setCopied(true);
        setTimeout(() => setCopied(false), 1200);
      } catch (fallbackErr) {
        console.error('Falha ao copiar:', err, fallbackErr);
      }
    }
  };

  const handleAction = async (action) => {
    const questionToAsk = String(question || '').trim();
    const searchToUse = String(searchTerm || '').trim();

    setLoading(true);
    setIsLoadingAnswer?.(true);
    setActiveAction(action);
    setResponse(null);
    setAnswerText?.('');
    setCopied(false);
    setWarmingUp(false);

    try {
      let result;
      const retryOpts = {
        maxRetries: 2,
        backoffMs: [800, 2000],
        onRetry: () => setWarmingUp(true),
      };

      switch (action) {
        case 'explain': {
          if (!currentPageData?.text) {
            const msg = 'Página vazia ou sem texto.';
            setResponse({ type: 'Erro', content: msg });
            setAnswerText?.(msg);
            break;
          }

          result = await requestWithColdStartRetry(
            () =>
              api.post('/api/explain-page', {
                fileId: pdfData.fileId,
                pageNumber: currentPage,
                text: currentPageData.text,
              }),
            retryOpts
          );

          const content =
            result.data.explanation ||
            result.data.summary ||
            'Sem resposta do servidor.';
          setResponse({
            type: 'Explicação da Página',
            content,
          });
          setAnswerText?.(content);
          break;
        }

        case 'summarize': {
          if (!pdfData?.text || !String(pdfData.text).trim()) {
            const msg =
              'O documento inteiro parece estar vazio ou sem texto extraído.';
            setResponse({ type: 'Erro', content: msg });
            setAnswerText?.(msg);
            break;
          }

          result = await requestWithColdStartRetry(
            () =>
              api.post('/api/summarize', {
                fileId: pdfData.fileId,
                text: pdfData.text,
              }),
            retryOpts
          );

          const content =
            result.data.summary ||
            result.data.explanation ||
            'Sem resposta do servidor.';
          setResponse({
            type: 'Resumo do Documento',
            content,
          });
          setAnswerText?.(content);
          break;
        }

        case 'search': {
          const query = searchToUse;
          if (!query) {
            const msg = 'Digite um termo para buscar';
            setResponse({ type: 'Erro', content: msg });
            setAnswerText?.(msg);
            break;
          }

          result = await requestWithColdStartRetry(
            () =>
              api.post('/api/search', {
                fileId: pdfData.fileId,
                query,
                text: pdfData.text,
                pages: pdfData.pages,
              }),
            retryOpts
          );

          if (result.data.results && result.data.results.length > 0) {
            const formatted = result.data.results
              .map((r, i) => `${i + 1}. Página ${r.pageNumber}: "${r.context}"`)
              .join('\n');

            const content = `Resultados para "${query}"\n\nEncontradas ${result.data.results.length} ocorrência(s):\n${formatted}`;
            setResponse({
              type: 'Busca no PDF',
              content,
            });
            setAnswerText?.(content);
          } else {
            const msg = 'Nenhuma ocorrência encontrada.';
            setResponse({ type: 'Busca no PDF', content: msg });
            setAnswerText?.(msg);
          }
          break;
        }

        case 'ask': {
          if (!questionToAsk) {
            const msg = 'Digite uma pergunta';
            setResponse({ type: 'Erro', content: msg });
            setAnswerText?.(msg);
            break;
          }

          const contextToUse =
            pdfData?.text && String(pdfData.text).trim()
              ? pdfData.text
              : currentPageData?.text;

          if (!contextToUse || !String(contextToUse).trim()) {
            const msg = 'Não foi possível encontrar texto do PDF para responder.';
            setResponse({ type: 'Erro', content: msg });
            setAnswerText?.(msg);
            break;
          }

          result = await requestWithColdStartRetry(
            () =>
              api.post('/api/ask', {
                fileId: pdfData.fileId,
                question: questionToAsk,
                context: contextToUse,
              }),
            retryOpts
          );

          const content = result.data.answer || '';
          setResponse({
            type: 'Resposta',
            content,
          });
          setAnswerText?.(content);
          setQuestion('');
          setChatHistory((prev) => [
            ...prev.slice(-9),
            {
              id: `q-${Date.now()}`,
              role: 'user',
              content: questionToAsk,
            },
            {
              id: `a-${Date.now()}-${Math.random()}`,
              role: 'assistant',
              content: content || 'Sem resposta da IA.',
            },
          ]);
          break;
        }

        default:
          break;
      }
    } catch (error) {
      console.error('Erro na requisição:', error);
      const errorMessage =
        error.response?.data?.error ||
        error.message ||
        'Erro ao processar solicitação';

      const overloaded =
        errorMessage.includes('503') ||
        errorMessage.includes('overloaded') ||
        errorMessage.includes('UNAVAILABLE');

      const msg = overloaded
        ? 'O serviço de IA está temporariamente sobrecarregado. Por favor, tente novamente em alguns instantes.'
        : errorMessage;

      setResponse({ type: 'Erro', content: msg });
      setAnswerText?.(msg);
      if (action === 'ask') {
        setChatHistory((prev) => [
          ...prev.slice(-9),
          ...(questionToAsk
            ? [
                {
                  id: `q-${Date.now()}`,
                  role: 'user',
                  content: questionToAsk,
                },
              ]
            : []),
          {
            id: `a-${Date.now()}-${Math.random()}`,
            role: 'assistant',
            content: msg,
          },
        ]);
      }
    } finally {
      setLoading(false);
      setIsLoadingAnswer?.(false);
      setActiveAction(null);
      setWarmingUp(false);
    }
  };

  return (
    <div className="ai-interface">
      <div className="ai-header">
        <div className="ai-header__title">
          <h3>
            <Bot size={18} /> Assistente de IA
          </h3>
          <span className={`ai-status ${loading ? 'loading' : ''}`}>
            {loading ? 'Analisando...' : 'Pronto para responder'}
          </span>
        </div>
      </div>

      <div className="action-buttons">
        <button
          type="button"
          className={`action-btn ${activeAction === 'explain' ? 'active' : ''}`}
          onClick={() => handleAction('explain')}
          disabled={loading}
        >
          <Lightbulb size={15} /> Entender Página
        </button>

        <button
          type="button"
          className={`action-btn ${activeAction === 'summarize' ? 'active' : ''}`}
          onClick={() => handleAction('summarize')}
          disabled={loading}
        >
          <FileText size={15} /> Resumir
        </button>

        <div className="search-row">
          <button
            type="button"
            className={`action-btn ${activeAction === 'search' ? 'active' : ''}`}
            onClick={() => handleAction('search')}
            disabled={loading}
            title="Buscar termo/palavra no PDF (sem IA)"
          >
            <Search size={15} /> Buscar Termo
          </button>

          <input
            className="search-term-input"
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Digite um termo..."
            disabled={loading}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAction('search');
              }
            }}
          />
        </div>
      </div>

      <div
        className={`response-container ${response?.content ? 'has-response' : ''}`}
      >
        <div className="response-header">
          <div className="response-title">
            <strong>{response?.type || ACTION_TITLES[activeAction] || 'Resposta IA'}</strong>
            {loading ? (
              <span className="response-loading" title="Gerando...">
                <Loader2 size={16} className="spinning-icon" />
              </span>
            ) : null}
          </div>

          <div className="response-toolbar">
            <button
              type="button"
              className="tool-btn"
              onClick={copyAnswer}
              disabled={loading || !String(response?.content || '').trim()}
              title="Copiar resposta"
            >
              <Copy size={16} />
              <span>{copied ? 'Copiado' : 'Copiar'}</span>
            </button>

            <button
              type="button"
              className="tool-btn danger"
              onClick={clearAnswer}
              disabled={loading || !response}
              title="Limpar resposta"
            >
              <Trash2 size={16} />
              <span>Limpar</span>
            </button>
          </div>
        </div>

        <div className="response-content">
          {chatHistory.length > 0 ? (
            <div className="chat-history">
              {chatHistory.map((item) => (
                <div
                  key={item.id}
                  className={`chat-message ${item.role === 'user' ? 'is-user' : 'is-ai'}`}
                >
                  <div className="chat-role">
                    {item.role === 'user' ? 'Você' : 'Assistente IA'}
                  </div>
                  <div className="chat-bubble">
                    {item.role === 'user' ? item.content : renderMarkdown(item.content)}
                  </div>
                </div>
              ))}
            </div>
          ) : null}

          {loading ? (
            <div className="inline-loading">
              <div className="spinner" />
              <div>
                {warmingUp
                  ? COLD_START_MESSAGE
                  : activeAction === 'search'
                    ? 'Buscando no PDF...'
                    : 'Gerando resposta...'}
              </div>
            </div>
          ) : response?.content ? (
            <div className="response-prose">{renderedResponse}</div>
          ) : (
            <div className="response-empty">
              <p>
                Sua resposta vai aparecer aqui. Use <strong>Entender Página</strong>,
                <strong> Resumir</strong>, <strong>Buscar Termo</strong> ou envie uma pergunta.
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="question-section">
        <textarea
          className="question-input"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder={askPlaceholder}
          disabled={loading}
          onKeyDown={(e) => {
            if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
              e.preventDefault();
              handleAction('ask');
            }
          }}
        />

        <div className="question-actions">
          <div className="suggestion-buttons">
            {SUGGESTED_QUESTIONS.map((item) => (
              <button
                key={item}
                type="button"
                className="suggestion-btn"
                onClick={() => setQuestion(item)}
                disabled={loading}
              >
                {item}
              </button>
            ))}
          </div>

          <button
            type="button"
            className="ask-button"
            onClick={() => handleAction('ask')}
            disabled={loading || !question.trim()}
          >
            Perguntar a IA
          </button>
        </div>
      </div>
    </div>
  );
};

export default AIInterface;
